import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';
import { trackReferralAttribution } from '../utils/referrals';

export default function CrewJoinPage({
  token,
  authUser,          // supabase auth user (preferred)
  onAuthRequired,
  onJoinSuccess
}) {
  const params = useParams();
  const navigate = useNavigate();

  const effectiveToken = useMemo(() => {
    return (token || params.token || '').toString().trim();
  }, [token, params.token]);

  const [crew, setCrew] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [referralTracked, setReferralTracked] = useState(false);

  const autoJoinRanRef = useRef(false);

  const safeAuthRequired = () => {
    if (effectiveToken) safeStorage.set('pendingCrewJoinToken', effectiveToken);

    if (typeof onAuthRequired === 'function') {
      onAuthRequired();
      return;
    }

    navigate('/profile', { replace: true });
  };

  const safeJoinSuccess = (crewObj) => {
    setStatus(`Joined ${crewObj?.name || 'crew'}! Redirecting…`);
    if (effectiveToken) {
      safeStorage.set('crewJoinJustJoined', effectiveToken);
      safeStorage.set('crewJoinSuppressUntil', String(Date.now() + 5 * 60 * 1000));
    }
    // Let parent clear tokens / close modals / update state...
    try {
      if (typeof onJoinSuccess === 'function') {
        onJoinSuccess(crewObj);
      }
    } catch (e) {
      console.warn('[CrewJoinPage] onJoinSuccess threw:', e);
    }

    // ...but ALWAYS navigate away after a successful join
    setTimeout(() => {
      navigate('/profile', { replace: true });
    }, 300);
  };

  // ---------- 1) Fetch crew by invite token ----------
  useEffect(() => {
    let cancelled = false;

    async function fetchCrewByToken(tok) {
      setLoading(true);
      setError(null);
      setStatus('');

      try {
        if (!tok) {
          setError('Invalid invite link (missing token).');
          setLoading(false);
          return;
        }

        const { data, error: qErr } = await supabase
          .from('crews')
          .select('id, name, description, emoji, member_count, invites_accepted, invite_token, created_by')
          .eq('invite_token', tok)
          .maybeSingle();

        if (cancelled) return;

        if (qErr) throw qErr;
        if (!data) {
          setError('Invalid or expired invite link.');
          setLoading(false);
          return;
        }

        // Optional: override member_count with live count from crew_members
        try {
          const { count } = await supabase
            .from('crew_members')
            .select('id', { count: 'exact', head: true })
            .eq('crew_id', data.id);
          if (!cancelled && typeof count === 'number') {
            data.member_count = count;
          }
        } catch {}

        setCrew(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error('[CrewJoinPage] fetchCrewByToken error:', e);
        setError('Failed to load crew details.');
        setLoading(false);
      }
    }

    fetchCrewByToken(effectiveToken);

    return () => {
      cancelled = true;
    };
  }, [effectiveToken]);

  useEffect(() => {
    if (!crew?.id || !crew?.created_by || referralTracked) return;

    let cancelled = false;
    (async () => {
      try {
        await trackReferralAttribution({
          inviterUserId: crew.created_by,
          sourceType: 'crew_invite',
          sourceId: crew.id,
        });
        if (!cancelled) setReferralTracked(true);
      } catch (err) {
        if (!cancelled) {
          console.warn('⚠️ Failed to track crew referral attribution:', err?.message || err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [crew?.id, crew?.created_by, referralTracked]);

  // If we just joined this crew, force redirect off join page
  useEffect(() => {
    const justJoined = safeStorage.get('crewJoinJustJoined');
    if (justJoined && effectiveToken && justJoined.toLowerCase() === effectiveToken.toLowerCase()) {
      safeStorage.remove('crewJoinJustJoined');
      navigate('/profile', { replace: true });
    }
  }, [effectiveToken, navigate]);

  // ---------- Helpers ----------
  async function getSessionUser() {
    // Prefer prop authUser if present
    if (authUser?.id) return authUser;

    const { data } = await supabase.auth.getSession();
    return data?.session?.user || null;
  }

  // Critical fix: ensure public.users row exists (prevents FK violation on crew_members.user_id)
  async function ensurePublicUserRowExists(user) {
    if (!user?.id) return false;

    // 1) Does the row exist?
    const { data: existing, error: readErr } = await supabase
      .from('users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (!readErr && existing?.id) return true;

    // 2) Create it (try progressively “fatter” payloads to handle your changing table schema)
    const usernameFallback =
      user.email?.split('@')?.[0]?.slice(0, 18) ||
      `User${user.id.slice(0, 6)}`;

    const attempts = [
      { id: user.id }, // minimal (works if only id column exists / defaults fill others)
      { id: user.id, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
      { id: user.id, username: usernameFallback },
      { id: user.id, username: usernameFallback, is_anonymous: false, coins: 0 },
    ];

    for (const payload of attempts) {
      // eslint-disable-next-line no-await-in-loop
      const { error: insErr } = await supabase.from('users').insert(payload);

      if (!insErr) return true;

      // If it's "already exists" (race), treat as success
      if (insErr.code === '23505') return true;

      // Otherwise try the next payload (schema may not accept some columns)
      console.warn('[CrewJoinPage] ensure user insert attempt failed:', payload, insErr);
    }

    return false;
  }

  // ---------- 2) Join flow ----------
  async function joinCrew() {
    if (!crew?.id) return;

    setError(null);
    setStatus('');

    const sessionUser = await getSessionUser();
    if (!sessionUser) {
      setStatus('You need to sign in to join.');
      safeAuthRequired();
      return;
    }

    setJoining(true);
    setStatus('Preparing your account…');

    try {
      // ✅ The FK fix: guarantee public.users row exists first
      const ok = await ensurePublicUserRowExists(sessionUser);
      if (!ok) {
        setJoining(false);
        setStatus('');
        setError("Couldn't finish setting up your profile. Please try again in a moment.");
        return;
      }

      setStatus('Joining crew…');

      // 1) Insert membership
      const { error: joinErr } = await supabase
        .from('crew_members')
        .insert({
          crew_id: crew.id,
          user_id: sessionUser.id,
          role: 'member',
          joined_at: new Date().toISOString()
        });

      // If already a member, treat as success
      if (joinErr) {
        // common unique violation codes (depends on constraint)
        if (joinErr.code === '23505') {
          console.log('[CrewJoinPage] already a member (unique violation) — treating as success');
        } else {
          throw joinErr;
        }
      }

      // Success
      safeStorage.remove('pendingCrewJoinToken');
      safeStorage.remove('gweffit_pending_deep_link');

      setJoining(false);
      setStatus('');
      safeJoinSuccess(crew);
    } catch (e) {
      console.error('[CrewJoinPage] joinCrew error:', e);
      setJoining(false);
      setStatus('');

      const msg =
        e?.message ||
        e?.hint ||
        'Failed to join crew. Please try again.';

      setError(msg);
    }
  }

  // Auto-join once after login if pending token matches
  useEffect(() => {
    if (autoJoinRanRef.current) return;
    if (!crew?.id) return;

    (async () => {
      const sessionUser = await getSessionUser();
      if (!sessionUser) return;

      const pending = safeStorage.get('pendingCrewJoinToken');
      if (pending && pending.toLowerCase() === effectiveToken.toLowerCase()) {
        autoJoinRanRef.current = true;
        joinCrew();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crew?.id, effectiveToken]);

  // ---------- UI ----------
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.bigEmoji}>⏳</div>
          <div style={styles.title}>Loading crew…</div>
          <div style={styles.sub}>Fetching invite details.</div>
        </div>
      </div>
    );
  }

  if (error && !crew) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.bigEmoji}>❌</div>
          <div style={styles.title}>Invite link issue</div>
          <div style={styles.sub}>{error}</div>
          <button style={styles.button} onClick={() => navigate('/', { replace: true })}>
            Go to GweffIT
          </button>
        </div>
      </div>
    );
  }

  if (!crew) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.bigEmoji}>🔍</div>
          <div style={styles.title}>Crew not found</div>
          <div style={styles.sub}>This invite link doesn’t exist or has expired.</div>
          <button style={styles.button} onClick={() => navigate('/', { replace: true })}>
            Go to GweffIT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.emojiCircle}>{crew.emoji || '👥'}</div>
        <div style={styles.title}>{crew.name}</div>
        {crew.description ? <div style={styles.sub}>{crew.description}</div> : null}

        <div style={styles.infoRow}>
          <div><strong>{crew.member_count || 0}</strong> members</div>
        </div>

        <div style={styles.banner}>You’ve been invited to join this crew.</div>

        {status ? <div style={styles.status}>{status}</div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}

        <button
          style={{ ...styles.button, opacity: joining ? 0.8 : 1 }}
          disabled={joining}
          onClick={joinCrew}
        >
          {joining ? 'Joining…' : `Join ${crew.name}`}
        </button>

        <div style={styles.tiny}>
          If you aren’t signed in, you’ll be prompted to sign in first.
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20
  },
  card: {
    background: '#fff',
    borderRadius: 24,
    maxWidth: 520,
    width: '100%',
    padding: 32,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center'
  },
  emojiCircle: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 46,
    margin: '0 auto 16px'
  },
  bigEmoji: { fontSize: 56, marginBottom: 12 },
  title: { fontSize: 28, fontWeight: 900, color: '#7c3aed', marginBottom: 10 },
  sub: { fontSize: 15, color: '#666', lineHeight: 1.5, marginBottom: 18 },
  infoRow: { display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 14, color: '#333' },
  banner: {
    background: '#f6f5ff',
    border: '1px solid #ebe7ff',
    color: '#4c1d95',
    padding: 12,
    borderRadius: 12,
    marginBottom: 14,
    fontWeight: 700
  },
  status: { color: '#111827', fontWeight: 700, marginBottom: 10 },
  error: { color: '#b91c1c', fontWeight: 700, marginBottom: 10 },
  button: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 17,
    fontWeight: 900,
    cursor: 'pointer'
  },
  tiny: { marginTop: 10, fontSize: 13, color: '#6b7280' }
};
